import {
  Activity,
  AppWindow,
  ArrowRight,
  Shield,
  TrendingUp,
  Users,
  FileText,
  UserCheck,
  Clock,
  GraduationCap,
  Mail,
  Languages
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import api from "../utils/api";
import useAuthStore from "../stores/authStore";

// UTC時間をローカルタイムゾーンに変換して「◯分前」表示
const formatTimeAgo = (utcTimeString) => {
  if (!utcTimeString) return "";

  try {
    // UTC時間文字列をDateオブジェクトに変換
    const utcDate = new Date(utcTimeString);
    const now = new Date();

    const diff = Math.floor((now - utcDate) / 1000);

    if (diff < 60) return `${diff}秒前`;
    if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}日前`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}ヶ月前`;
    return `${Math.floor(diff / 31536000)}年前`;
  } catch (error) {
    console.error("時間変換エラー:", error);
    return utcTimeString;
  }
};

// タイムゾーン情報を取得（デバッグ用）
// const getTimezoneInfo = () => {
//   const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
//   const now = new Date();

//   return {
//     timezone,
//     currentTime: now.toLocaleString("ja-JP"),
//     utcOffset: now.getTimezoneOffset(),
//     timezoneName: timezone,
//   };
// };

// アプリケーション名に基づいて色を取得
const getAppColor = (appName) => {
  const colorMap = {
    resume: "bg-blue-500",
    evaluation: "bg-green-500",
    interview: "bg-purple-500",
    project: "bg-orange-500",
    elearning: "bg-indigo-500",
    webmail: "bg-red-500",
    translation: "bg-cyan-500",
  };
  return colorMap[appName] || "bg-gray-500";
};

// アプリケーション名に基づいてアイコンを取得
const getAppIcon = (appName) => {
  const iconMap = {
    resume: FileText,
    evaluation: UserCheck,
    interview: Users,
    project: Clock,
    elearning: GraduationCap,
    webmail: Mail,
    translation: Languages,
  };
  return iconMap[appName] || AppWindow;
};

const DashboardPage = () => {
  const currentUser = useAuthStore((state) => state.user);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalApps: 0,
    activeSessions: 0,
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apps, setApps] = useState([]);
  // const [timezoneInfo] = useState(null);
  const [launchingApp, setLaunchingApp] = useState(null);

  // アプリケーション起動処理
  const handleAppLaunch = (appName) => {
    setLaunchingApp(appName);
    
    // webmail / interview：不做 OAuth，直接打开目标站（与 launch API 一致）
    const directUrls = {
      webmail: "https://mailui.arjin.pro",
      interview: "https://interview.arjin.pro",
    };
    const direct = directUrls[appName];
    setTimeout(() => {
      if (direct) {
        window.open(direct, "_blank");
      } else {
        window.open(`/api/sso/launch/${appName}`, "_blank");
      }
      setLaunchingApp(null);
    }, 500);
  };

  useEffect(() => {
    // タイムゾーン情報を取得（現在は使用していない）
    // setTimezoneInfo(getTimezoneInfo());

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        // 統計情報取得
        const statsRes = await api.get("/dashboard/stats");
        setStats(statsRes.data);
        // アクティビティ取得（管理者のみ）
        if (currentUser?.role === "admin") {
          const activityRes = await api.get("/dashboard/activity");
          setRecentActivity(activityRes.data.activities);
        }
        // アプリ情報取得
        const appsRes = await api.get("/dashboard/apps");
        setApps(appsRes.data.apps);
      } catch (err) {
        console.error("ダッシュボードデータ読み込みエラー:", err);
        setError("ダッシュボードデータの読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [currentUser?.role]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">ダッシュボードを読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>再試行</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">ダッシュボード</h1>
        <p className="text-muted-foreground">
          ARJINシステムの概要と統計情報
        </p>
      </div>

      {/* 統計カード */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">総ユーザー数</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              アクティブユーザー
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              登録アプリケーション
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalApps}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              アクティブセッション
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeSessions}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        {/* アプリケーション一覧 */}
        <Card className={currentUser?.role === "admin" ? "col-span-4" : "col-span-7"}>
          <CardHeader>
            <CardTitle>アプリケーション</CardTitle>
            <CardDescription>
              登録されているアプリケーションとその利用状況
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {apps.map((app) => {
                const AppIcon = getAppIcon(app.name);
                const isLaunching = launchingApp === app.name;
                return (
                  <div
                    key={app.id}
                    className={`flex items-center space-x-4 rounded-lg border p-4 transition-colors ${
                      isLaunching 
                        ? 'bg-accent/50 cursor-wait' 
                        : 'hover:bg-accent/50 cursor-pointer'
                    }`}
                    onClick={() => !isLaunching && handleAppLaunch(app.name)}
                  >
                    <div className={`p-2 rounded-lg ${getAppColor(app.name)}`}>
                      {isLaunching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <AppIcon className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {app.display_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isLaunching 
                          ? 'アプリケーションを起動中...' 
                          : app.description
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(app.name === "webmail" || app.name === "interview") ? "" : app.users + " ユーザー"}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      disabled={isLaunching}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLaunching) {
                          handleAppLaunch(app.name);
                        }
                      }}
                    >
                      {isLaunching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                      ) : (
                        <ArrowRight className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* 最近のアクティビティ - 管理者のみ表示 */}
        {currentUser?.role === "admin" && (
          <Card className="col-span-3 h-full flex flex-col">
            <CardHeader>
              <CardTitle>最近のアクティビティ</CardTitle>
              <CardDescription>システム内での最近のログイン活動（管理者専用）</CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {activity.user}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.action}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.app}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTimeAgo(activity.time)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
